// layouts/catalogos/sellos/editar-sello.jsx
import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";

import Grid from "@mui/material/Grid";
import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";

import { getSelloById, updateSello } from "../services/sellos";
import { listGrupos } from "../services/grupos";

import FormPage from "ui/FormPage";
import { PrimaryButton, OutlineButton } from "ui/Buttons";
import FormTextField from "ui/FormTextField";

/* ---------------- helpers ---------------- */
function parseApiErrors(err) {
  const out = [];
  const res = err?.response;
  const d = res?.data ?? err;

  const push = (m) => {
    if (m == null) return;

    if (typeof m === "string") {
      out.push(m);
      return;
    }

    if (typeof m === "number" || typeof m === "boolean") {
      out.push(String(m));
      return;
    }

    if (Array.isArray(m)) {
      m.forEach(push);
      return;
    }

    if (typeof m === "object") {
      if (Array.isArray(m.errors)) m.errors.forEach(push);
      if (Array.isArray(m.message)) m.message.forEach(push);
      if (Array.isArray(m.details)) m.details.forEach(push);
      if (Array.isArray(m.issues)) m.issues.forEach(push);

      if (typeof m.message === "string") out.push(m.message);
      if (typeof m.msg === "string") out.push(m.msg);
      if (typeof m.error === "string") out.push(m.error);

      if (m.instancePath || m.path || m.dataPath) {
        const path = String(m.instancePath || m.path || m.dataPath || "")
          .replace(/^\//, "")
          .replace(/\//g, ".");
        const msg = m.message || m.msg || "error";
        out.push(path ? `${path}: ${msg}` : msg);
      }

      if (out.length === 0) {
        try {
          out.push(JSON.stringify(m));
        } catch {
          out.push(String(m));
        }
      }

      return;
    }

    out.push(String(m));
  };

  push(d);

  if (res?.statusText && res.statusText !== "OK") out.push(res.statusText);
  if (res?.status) out.push(`HTTP ${res.status}`);

  if (out.length === 0) out.push("Error del servidor");

  return [...new Set(out)].filter(Boolean);
}

const normalizeGroups = (res) => {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.rows)) return res.rows;
  if (Array.isArray(res?.page)) return res.page;
  if (Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res?.results)) return res.results;
  return [];
};

const groupLabel = (opt) =>
  opt?.name ??
  opt?.nombre ??
  (opt?.id != null || opt?._id != null ? String(opt.id ?? opt._id) : "");

/* ----------------------------------------- */

export default function EditarSello() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [selectedGroupIds, setSelectedGroupIds] = useState([]);
  const [createdAt, setCreatedAt] = useState("");

  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState([]);

  const [groups, setGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);

  useEffect(() => {
    let cancel = false;

    const fetchData = async () => {
      setLoading(true);
      setLoadingGroups(true);
      setErrors([]);

      try {
        const [s, groupsRes] = await Promise.all([
          getSelloById(id),
          listGrupos({
            __limit: 1000,
            __offset: 0,
            __sort: { name: 1 },
          }),
        ]);

        if (cancel) return;

        setName(s?.name ?? s?.nombre ?? "");

        const incomingGroupIds = Array.isArray(s?.groups)
          ? s.groups
              .map((g) =>
                typeof g === "object" && g !== null
                  ? g.id ?? g._id
                  : g
              )
              .filter(
                (id) =>
                  id !== null &&
                  id !== undefined &&
                  id !== ""
              )
              .map(String)
          : s?.group_id != null
          ? [String(s.group_id)]
          : s?.groupId != null
          ? [String(s.groupId)]
          : s?.group?.id != null
          ? [String(s.group.id)]
          : s?.grupo?.id != null
          ? [String(s.grupo.id)]
          : [];

        setSelectedGroupIds(incomingGroupIds);

        setCreatedAt(s?.created_at ?? s?.createdAt ?? "");

        setGroups(normalizeGroups(groupsRes));
      } catch (e) {
        if (!cancel) {
          const errs = parseApiErrors(e);
          setErrors(Array.isArray(errs) ? errs : [String(errs || "Error")]);
        }
      } finally {
        if (!cancel) {
          setLoading(false);
          setLoadingGroups(false);
        }
      }
    };

    fetchData();

    return () => {
      cancel = true;
    };
  }, [id]);

  const normalizedGroupIds = useMemo(
    () =>
      Array.isArray(selectedGroupIds)
        ? selectedGroupIds
            .map(Number)
            .filter((id) => Number.isFinite(id) && id > 0)
        : [],
    [selectedGroupIds]
  );

  const isValid = useMemo(
    () =>
      !!name.trim() &&
      normalizedGroupIds.length > 0,
    [name, normalizedGroupIds]
  );

  const selectedGroups = groups.filter((g) =>
    selectedGroupIds.includes(String(g.id ?? g._id))
  );

  const onSubmit = async (e) => {
    e.preventDefault();

    if (!isValid) {
      const local = [];

      if (!name.trim()) {
        local.push("El nombre es obligatorio.");
      }

      if (normalizedGroupIds.length === 0) {
        local.push("Selecciona al menos un grupo.");
      }

      setErrors(local);
      return;
    }

    setSaving(true);
    setErrors([]);

    try {
      const payload = {
        name: name.trim(),
        groups: normalizedGroupIds,
      };

      await updateSello(id, payload);
      navigate("/catalogos/sellos", { replace: true });
    } catch (e2) {
      const errs = parseApiErrors(e2);
      setErrors(Array.isArray(errs) ? errs : [String(errs || "Error")]);
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormPage
      title="Editar sello"
      onBack={() => navigate("/catalogos/sellos")}
      contentMaxWidth={900}
      actions={
        <>
          <PrimaryButton
            type="submit"
            form="form-sello"
            disabled={!isValid || saving || loading}
          >
            {saving ? "Guardando..." : "Guardar"}
          </PrimaryButton>

          <OutlineButton onClick={() => navigate("/catalogos/sellos")}>
            Cancelar
          </OutlineButton>
        </>
      }
    >
      {errors.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errors.length === 1 ? (
            errors[0]
          ) : (
            <ul style={{ margin: 0, paddingLeft: "1.2rem" }}>
              {errors.map((m, i) => (
                <li key={i}>{m}</li>
              ))}
            </ul>
          )}
        </Alert>
      )}

      <form id="form-sello" onSubmit={onSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <FormTextField
              label="Nombre"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              disabled={loading}
            />
          </Grid>

          <Grid item xs={12}>
            <Autocomplete
              multiple
              disableCloseOnSelect
              options={groups}
              loading={loadingGroups}
              value={selectedGroups}
              isOptionEqualToValue={(opt, val) =>
                String(opt?.id ?? opt?._id) ===
                String(val?.id ?? val?._id)
              }
              onChange={(_e, selected) => {
                setSelectedGroupIds(
                  selected.map((g) =>
                    String(g.id ?? g._id)
                  )
                );
              }}
              getOptionLabel={groupLabel}
              renderInput={(params) => (
                <FormTextField
                  {...params}
                  label="Grupos*"
                  placeholder={
                    loadingGroups
                      ? "Cargando grupos..."
                      : groups.length
                      ? "Selecciona al menos un grupo"
                      : "No hay grupos disponibles"
                  }
                  disabled={loading}
                />
              )}
              noOptionsText={
                loadingGroups ? "Cargando..." : "Sin resultados"
              }
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormTextField label="Creado" value={createdAt} disabled />
          </Grid>
        </Grid>
      </form>
    </FormPage>
  );
}